import * as React from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ArrowUp, Check, Copy, FileText, History, MessageSquare, Plus, RotateCw, Scale, Trash2, TriangleAlert, X } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { ApiError, apiErrorDetail, askRegulation, clearLookupChats, deleteLookupChat, getDisputesSheetLink, getDisputesSheetRows, importLookupChats, saveLookupChat } from "@hp/api-client";
import type { AssistantSource, RegulationAskResponse, RegulationCitation, RegulationResult, RegulationTurn } from "@hp/api-client";
import { Dialog } from "@hp/ui";
import { SourceDocumentViewer } from "../components/SourceDocumentViewer";
import { GenieFeedbackButton } from "../components/GenieFeedbackButton";
import { useAuth } from "../auth/AuthContext";
import { useAlertSetting } from "../lib/useAlertSettings";
import { SHOW_STARTER_PROMPTS } from "../lib/demoMode";
import {
  clearConversations,
  dayLabel,
  deleteConversation,
  loadConversations,
  deletedHere,
  forgetDeletion,
  pendingDeletions,
  rememberDeletion,
  replaceWithServerConversations,
  newConversationId,
  newMessageId,
  saveConversation,
} from "../lib/regulationChatHistory";
import type { StoredConversation } from "../lib/regulationChatHistory";

/**
 * HP Genie: a ChatGPT-style chat over the indexed regulatory documents.
 *
 * The whole message is ONE question (it is never split, whatever its lines or question marks). The reply is written out
 * in full: the answer, a detailed plain explanation read from the passages, and the sources with their
 * passages. The confidence score and the chain of orders are not shown. Every source is a button
 * that opens the page the answer was read from, with the passage highlighted when its exact page is known.
 */

const EXAMPLES = [
  "What is the annual fixed charge for the Madhya Pradesh 30% share in financial year 2024-25?",
  "Is availability on the Uttar Pradesh contract tested monthly or annually?",
  "What is the Uttar Pradesh energy charge in August 2026 and how is it built?",
];

type ChatMessage =
  | { id: string; role: "user"; text: string }
  | { id: string; role: "assistant"; response: RegulationAskResponse }
  | { id: string; role: "error"; text: string; retryable: boolean };

const newId = newMessageId;

interface OpenCitation {
  source: AssistantSource;
  passage?: { text: string; page: number; searchWholeDocument?: boolean };
  startPage?: number | null;
}

function toOpenCitation(c: RegulationCitation): OpenCitation {
  // An "ongoing_dispute" source is listed without a link, so it never reaches here; the mapping only satisfies the type.
  const source: AssistantSource = { type: c.type === "ongoing_dispute" ? "source_document" : c.type, id: c.id, label: c.label };
  // A paragraph of a read order records its real page, so the viewer can open that page and highlight the passage.
  // A part of a contract or rule (an indexed document section) is only as good as the page breaks of the text it was indexed
  // from, so its page is a guess: the viewer searches the whole file for the passage and highlights it where it is found.
  if (c.type === "source_document" && c.page && c.passage) {
    return { source, passage: { text: c.passage, page: c.page } };
  }
  if (c.type === "document_section" && c.passage) {
    return { source, passage: { text: c.passage, page: c.page && c.page > 0 ? c.page : 1, searchWholeDocument: true }, startPage: c.page && c.page > 1 ? c.page : null };
  }
  return { source, startPage: c.page && c.page > 1 ? c.page : null };
}


/** The rows cited, from an id like "UP:1-11" (first and last row). */
function citedRows(id: string): [number, number] {
  const range = id.split(":")[1] ?? "";
  const [from, to] = range.split("-").map(Number);
  return [from || 0, to || from || 0];
}

/** The team's spreadsheet shown in the page: every row of the state's list, the cited rows highlighted. */
function DisputesSheetViewer({ state, citedId, onClose }: { state: string; citedId: string; onClose: () => void }) {
  const sheet = useQuery({ queryKey: ["disputes-sheet-rows", state], queryFn: ({ signal }) => getDisputesSheetRows(state, signal) });
  const [from, to] = citedRows(citedId);
  const firstCited = React.useRef<HTMLTableRowElement | null>(null);
  React.useEffect(() => {
    firstCited.current?.scrollIntoView({ block: "center" });
  }, [sheet.data]);
  const download = useMutation({
    mutationFn: () => getDisputesSheetLink(state),
    onSuccess: (link) => {
      if (link !== null) window.open(link.url, "_blank", "noopener");
    },
  });
  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={sheet.data?.file_name ?? "Ongoing disputes list"}
      description={`Rows ${from}${to !== from ? ` to ${to}` : ""} are highlighted. The sheet is shown as it was read.`}
      titleId="disputes-sheet-title"
      size="lg"
      footer={
        <button type="button" onClick={() => download.mutate()} className="text-xs font-semibold text-emerald-800 underline-offset-2 hover:underline">
          Download the .xlsx
        </button>
      }
    >
      {sheet.isPending ? <p className="text-sm text-zinc-500">Loading the sheet...</p> : null}
      {sheet.isError ? <p className="text-sm text-red-700">The sheet could not be loaded.</p> : null}
      {sheet.data ? (
        <div className="max-h-[65vh] overflow-auto rounded-md border border-zinc-200">
          <table className="min-w-full border-collapse text-left text-xs">
            <thead className="sticky top-0 bg-zinc-100">
              <tr>
                {sheet.data.columns.map((h) => (
                  <th key={h} className="border-b border-zinc-200 px-2 py-1.5 font-semibold text-zinc-700">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sheet.data.rows.map((r) => {
                const cited = r.row_no >= from && r.row_no <= to;
                return (
                  <tr key={r.row_no} ref={cited && r.row_no === from ? firstCited : undefined} className={cited ? "bg-amber-100" : "odd:bg-white even:bg-zinc-50/50"}>
                    {r.cells.map((cell, i) => (
                      <td key={i} className="min-w-[8rem] max-w-[22rem] border-b border-zinc-100 px-2 py-1.5 align-top text-zinc-800 whitespace-pre-wrap">
                        {cell}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
    </Dialog>
  );
}

/** The team's spreadsheet as a source: opens the in-page sheet viewer with the cited rows highlighted. */
function DisputesSheetLink({ state, citedId, fileName }: { state: string; citedId: string; fileName: string }) {
  const [open, setOpen] = React.useState(false);
  return (
    <div className="mt-0.5 flex flex-wrap items-center gap-2 text-[0.6875rem] text-zinc-500">
      <button
        type="button"
        onClick={() => setOpen(true)}
        title={`View ${fileName}`}
        className="inline-flex items-center gap-1 font-semibold text-emerald-800 underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400"
      >
        <FileText className="h-3.5 w-3.5 flex-none" aria-hidden />
        {fileName}
      </button>
      <span>spreadsheet (.xlsx)</span>
      {open ? <DisputesSheetViewer state={state} citedId={citedId} onClose={() => setOpen(false)} /> : null}
    </div>
  );
}

function SourceList({ citations, onOpen }: { citations: RegulationCitation[]; onOpen: (c: RegulationCitation) => void }) {
  if (citations.length === 0) {
    return <p className="text-xs text-zinc-400">No source found.</p>;
  }
  return (
    <ol className="space-y-2">
      {citations.map((c, i) =>
        c.type === "ongoing_dispute" ? (
          // The team's list is read into rows, and the spreadsheet it was read from is kept: the source names the rows, shows their own words and opens the sheet.
          <li key={`${c.key}-${c.id}`} className="rounded-lg border border-emerald-100 bg-emerald-50/50 px-3 py-2">
            <div className="text-xs font-semibold text-zinc-800">
              [{i + 1}] {c.label}
            </div>
            {c.file_name ? <DisputesSheetLink state={c.id.split(":")[0] ?? ""} citedId={c.id} fileName={c.file_name} /> : null}
            {c.passage ? <p className="mt-1 text-[0.6875rem] leading-snug text-zinc-600">{c.passage}</p> : null}
          </li>
        ) : (
        <li key={`${c.key}-${c.id}`} className="rounded-lg border border-sky-100 bg-sky-50/50 px-3 py-2">
          <button
            type="button"
            onClick={() => onOpen(c)}
            title={`Open ${c.file_name}${c.page ? `, page ${c.page}` : ""}`}
            className="inline-flex max-w-full items-start gap-1.5 text-left text-xs font-semibold text-sky-800 underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400"
          >
            <FileText className="mt-px h-3.5 w-3.5 flex-none" aria-hidden />
            <span className="break-words">
              [{i + 1}] {c.label}
            </span>
          </button>
          <div className="mt-0.5 text-[0.6875rem] text-zinc-500">
            {c.file_name}
            {c.page ? ` · page ${c.page}` : ""}
          </div>
          {c.passage ? <blockquote className="mt-1 border-l-2 border-sky-300 pl-2 text-[0.6875rem] italic leading-snug text-zinc-700">{c.passage.length > 320 ? `${c.passage.slice(0, 320)}…` : c.passage}</blockquote> : null}
        </li>
        ),
      )}
    </ol>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return <h4 className="mb-1.5 text-[0.6875rem] font-semibold uppercase tracking-wide text-zinc-500">{children}</h4>;
}

/** The explanation is markdown: a bold figure, a table for a list or a comparison, arithmetic one step to a line. */
// The text is model output built from document text, so only what an answer needs is drawn: no images (a crafted document could make the
// answer load a URL that carries data out) and no links. A disallowed element is unwrapped, its text kept.
const DETAIL_ELEMENTS = ["p", "h3", "strong", "em", "ul", "ol", "li", "table", "thead", "tbody", "tr", "th", "td", "br"];
const detailComponents: Components = {
  p: ({ children }) => <p className="mt-2 text-sm leading-relaxed text-zinc-700">{children}</p>,  // markdown sets the breaks; a wrapped line is not one
  h3: ({ children }) => <h3 className="mt-3 text-sm font-semibold text-zinc-900">{children}</h3>,
  strong: ({ children }) => <strong className="font-semibold text-zinc-900">{children}</strong>,
  ul: ({ children }) => <ul className="mt-2 list-disc space-y-1 pl-5 text-sm leading-relaxed text-zinc-700">{children}</ul>,
  ol: ({ children }) => <ol className="mt-2 list-decimal space-y-1 pl-5 text-sm leading-relaxed text-zinc-700">{children}</ol>,
  table: ({ children }) => (
    <div className="mt-2 overflow-x-auto rounded-lg border border-zinc-200">
      <table className="w-full border-collapse text-left text-xs">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-zinc-50 text-zinc-600">{children}</thead>,
  th: ({ children }) => <th className="border-b border-zinc-200 px-2 py-1.5 font-semibold">{children}</th>,
  td: ({ children }) => <td className="border-t border-zinc-100 px-2 py-1.5 align-top text-zinc-700">{children}</td>,
};

/** One question's reply, written out in full: the answer, the detailed explanation and the sources. */
function AnswerCard({ result, index, numbered, onOpen }: { result: RegulationResult; index: number; numbered: boolean; onOpen: (c: RegulationCitation) => void }) {
  const details = result.details ?? [];
  return (
    <article className="space-y-3 rounded-xl border border-zinc-200 bg-white px-4 py-3.5">
      {/* The question is not repeated here: it is in the person's own message just above. */}
      {numbered ? <div className="text-[0.6875rem] font-semibold uppercase tracking-wide text-zinc-400">Answer {index + 1}</div> : null}
      <div>
        {/* The answer line is read with tables too: a table that reaches it is laid out as a table, never shown as a bold run of "|". */}
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            ...detailComponents,
            p: ({ children }) => <p className={["text-sm font-semibold leading-snug", result.found ? "text-zinc-900" : "text-zinc-500"].join(" ")}>{children}</p>,
            strong: ({ children }) => <strong className="font-bold">{children}</strong>,
          }}
          allowedElements={DETAIL_ELEMENTS}
          unwrapDisallowed
        >
          {result.answer}
        </ReactMarkdown>
        {details.map((paragraph, i) => (
          <ReactMarkdown key={i} remarkPlugins={[remarkGfm]} components={detailComponents} allowedElements={DETAIL_ELEMENTS} unwrapDisallowed>
            {paragraph}
          </ReactMarkdown>
        ))}
      </div>
      {/* A question asked back (which contract is meant) states nothing, so it has no sources to list. */}
      {result.method === "clarify" && result.citations.length === 0 ? null : (
        <section>
          <SectionTitle>Where this comes from</SectionTitle>
          <SourceList citations={result.citations} onOpen={onOpen} />
        </section>
      )}
    </article>
  );
}

function AnswerList({ results, onOpen }: { results: RegulationResult[]; onOpen: (c: RegulationCitation) => void }) {
  const numbered = results.length > 1;
  return (
    <div className="space-y-3">
      {results.map((result, index) => (
        <AnswerCard key={`${index}-${result.question}`} result={result} index={index} numbered={numbered} onOpen={onOpen} />
      ))}
    </div>
  );
}

/** The reply as plain text (no table), so it pastes cleanly into an email, a note or a document. */
function toText(results: RegulationResult[]): string {
  const numbered = results.length > 1;
  return results
    .map((r, i) => {
      const lines = [numbered ? `Question ${i + 1}: ${r.question}` : `Question: ${r.question}`, "", `Answer: ${r.answer}`];
      if (r.details && r.details.length > 0) lines.push("", ...r.details.flatMap((d) => [d, ""]).slice(0, -1));
      lines.push("", "Sources:", ...(r.citations.length > 0 ? r.citations.map((c, n) => `[${n + 1}] ${c.label} (${c.file_name}${c.page ? `, page ${c.page}` : ""})`) : ["No source found."]));
      return lines.join("\n");
    })
    .join("\n\n----\n\n");
}

/**
 * The whole chat so far as (question, full answer) pairs, oldest first: sent with every new question, in full, so HP Genie reads
 * each follow-up in the conversation's light until the chat ends. A question that got an error has no answer to pass.
 */
function chatTurns(messages: ChatMessage[]): RegulationTurn[] {
  const turns: RegulationTurn[] = [];
  messages.forEach((m, i) => {
    if (m.role !== "user") return;
    const next = messages[i + 1];
    turns.push({ question: m.text, answer: next && next.role === "assistant" ? toText(next.response.results) : "" });
  });
  return turns;
}

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Older browsers and non-secure pages: fall back to a hidden text box.
    const box = document.createElement("textarea");
    box.value = text;
    box.style.position = "fixed";
    box.style.opacity = "0";
    document.body.appendChild(box);
    box.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(box);
    return ok;
  }
}

function ActionButton({
  onClick,
  icon: Icon,
  label,
  disabled,
}: {
  onClick: () => void;
  icon: LucideIcon;
  label: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-800 disabled:cursor-not-allowed disabled:opacity-50"
    >
      <Icon className="h-3.5 w-3.5" aria-hidden />
      {label}
    </button>
  );
}

function CopyButton({ text, label = "Copy" }: { text: string; label?: string }) {
  const [copied, setCopied] = React.useState(false);
  return (
    <ActionButton
      onClick={() => {
        void copyText(text).then((ok) => {
          if (!ok) return;
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1600);
        });
      }}
      icon={copied ? Check : Copy}
      label={copied ? "Copied" : label}
    />
  );
}

/** The Feedback button under each answer follows the org-wide setting (Settings > HP Genie answers); it is off until someone switches it on. */
function useShowFeedbackButton(): boolean {
  return useAlertSetting("show_genie_feedback") === true;
}

function AssistantTurn({
  response,
  onOpen,
  onRetry,
  busy,
}: {
  response: RegulationAskResponse;
  onOpen: (c: RegulationCitation) => void;
  onRetry: () => void;
  busy: boolean;
}) {
  const results = response.results;
  const showFeedback = useShowFeedbackButton();
  return (
    <div className="space-y-2">
      {response.notice ? (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          <TriangleAlert className="mt-0.5 h-3.5 w-3.5 flex-none" aria-hidden />
          <span>{response.notice}</span>
        </div>
      ) : null}
      <AnswerList results={results} onOpen={onOpen} />
      <div className="flex flex-wrap items-start gap-1 px-1">
        <CopyButton text={toText(results)} label="Copy answer" />
        <ActionButton onClick={onRetry} icon={RotateCw} label="Retry" disabled={busy} />
        {showFeedback ? <GenieFeedbackButton results={results} /> : null}
      </div>
    </div>
  );
}

/** What HP Genie is doing while it researches, by how long it has been at it (the steps' usual timing; the server reports no progress). */
const RESEARCH_STAGES: Array<[number, string]> = [
  [0, "Planning the research…"],
  [6, "Searching the contracts, orders and rules…"],
  [25, "Reading what each passage says…"],
  [50, "Checking what could limit or overturn the answer…"],
  [75, "Looking for what is still missing…"],
  [95, "Writing the answer…"],
];

function Thinking() {
  const [seconds, setSeconds] = React.useState(0);
  React.useEffect(() => {
    const started = Date.now();
    const timer = window.setInterval(() => setSeconds(Math.floor((Date.now() - started) / 1000)), 1000);
    return () => window.clearInterval(timer);
  }, []);
  // Every question is researched in depth (about a minute and a half), so the steps are shown from the start.
  const stage = [...RESEARCH_STAGES].reverse().find(([from]) => seconds >= from)?.[1] ?? RESEARCH_STAGES[0][1];
  return (
    <div className="flex items-center gap-1.5 px-1 py-2 text-sm text-zinc-500" role="status" aria-live="polite">
      <span className="h-2 w-2 animate-pulse rounded-full bg-zinc-400" />
      <span className="h-2 w-2 animate-pulse rounded-full bg-zinc-400 [animation-delay:150ms]" />
      <span className="h-2 w-2 animate-pulse rounded-full bg-zinc-400 [animation-delay:300ms]" />
      <span className="ml-2">{stage}</span>
      <span className="tabular-nums text-zinc-400">{seconds}s</span>
    </div>
  );
}

/** Past conversations of this user, newest first, grouped by day. Opening one continues it; deleting one removes it on every device. */
function HistoryPanel({
  conversations,
  activeId,
  onOpen,
  onDelete,
  onClearAll,
  onClose,
}: {
  conversations: StoredConversation[];
  activeId: string;
  onOpen: (c: StoredConversation) => void;
  onDelete: (id: string) => void;
  onClearAll: () => void;
  onClose: () => void;
}) {
  const groups: { label: string; items: StoredConversation[] }[] = [];
  for (const c of conversations) {
    const label = dayLabel(c.updatedAt);
    const last = groups[groups.length - 1];
    if (last && last.label === label) last.items.push(c);
    else groups.push({ label, items: [c] });
  }
  return (
    <aside
      aria-label="Chat history"
      className="absolute right-0 top-10 z-20 flex max-h-[70dvh] w-80 max-w-[92vw] flex-col rounded-xl border border-zinc-200 bg-white shadow-lg"
    >
      <div className="flex items-center justify-between border-b border-zinc-100 px-3 py-2">
        <h3 className="text-sm font-semibold text-zinc-900">History</h3>
        <div className="flex items-center gap-1">
          {conversations.length > 0 ? (
            <button
              type="button"
              onClick={() => {
                if (window.confirm("Delete all your saved chats? They are removed on every device, not only this browser.")) onClearAll();
              }}
              className="rounded-md px-2 py-1 text-xs font-medium text-zinc-500 hover:bg-zinc-100 hover:text-red-700"
            >
              Clear all
            </button>
          ) : null}
          <button type="button" onClick={onClose} aria-label="Close history" className="rounded-md p-1 text-zinc-500 hover:bg-zinc-100">
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-2">
        {conversations.length === 0 ? (
          <p className="px-2 py-6 text-center text-xs text-zinc-500">No saved chats yet. Each chat you start is saved here so you can come back to it.</p>
        ) : (
          groups.map((g) => (
            <div key={g.label} className="mb-2">
              <div className="px-2 pb-1 pt-2 text-[0.6875rem] font-semibold uppercase tracking-wide text-zinc-400">{g.label}</div>
              <ul className="space-y-0.5">
                {g.items.map((c) => (
                  <li key={c.id} className="group flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => onOpen(c)}
                      aria-current={c.id === activeId ? "true" : undefined}
                      title={`${c.title} · ${new Date(c.updatedAt).toLocaleString()}`}
                      className={[
                        "flex min-w-0 flex-1 items-start gap-2 rounded-lg px-2 py-1.5 text-left text-sm transition-colors",
                        c.id === activeId ? "bg-sky-50 text-sky-900" : "text-zinc-700 hover:bg-zinc-100",
                      ].join(" ")}
                    >
                      <MessageSquare className="mt-0.5 h-3.5 w-3.5 flex-none text-zinc-400" aria-hidden />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate">{c.title}</span>
                        <span className="block text-[0.6875rem] text-zinc-400">
                          {c.messages.filter((m) => m.role === "user").length} question{c.messages.filter((m) => m.role === "user").length === 1 ? "" : "s"} ·{" "}
                          {new Date(c.updatedAt).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
                        </span>
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => onDelete(c.id)}
                      aria-label={`Delete chat: ${c.title}`}
                      className="flex-none rounded-md p-1.5 text-zinc-400 opacity-0 transition-opacity hover:bg-zinc-100 hover:text-red-700 focus:opacity-100 group-hover:opacity-100"
                    >
                      <Trash2 className="h-3.5 w-3.5" aria-hidden />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))
        )}
      </div>
    </aside>
  );
}

export function RegulationChatPage() {
  const { user } = useAuth();
  const userKey = user?.id ?? "anonymous";
  const [conversationId, setConversationId] = React.useState(newConversationId);
  const [history, setHistory] = React.useState<StoredConversation[]>(() => loadConversations(userKey));
  const [showHistory, setShowHistory] = React.useState(false);
  const [messages, setMessages] = React.useState<ChatMessage[]>([]);
  const [draft, setDraft] = React.useState("");
  const [open, setOpen] = React.useState<OpenCitation | null>(null);
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLTextAreaElement>(null);


  // The chat a question was asked in: a reply that arrives after the person moved to another chat is dropped, never appended there.
  const conversationRef = React.useRef(conversationId);
  conversationRef.current = conversationId;
  // Opening a past chat shows it as it was; it is not a change to save (saving would stamp it as updated now, on every device).
  const skipSaveRef = React.useRef(false);

  const ask = useMutation({
    mutationFn: ({ message, history, turns }: { message: string; history: string[]; turns: RegulationTurn[]; chatId: string }) =>
      askRegulation(message, { history, turns }),
    onSuccess: (response, variables) => {
      if (variables.chatId !== conversationRef.current) return;
      setMessages((prev) => [...prev, { id: newId(), role: "assistant", response }]);
    },
    onError: (error, variables) => {
      if (variables.chatId !== conversationRef.current) return;
      const expired = error instanceof ApiError && (error.status === 401 || error.status === 403);
      // The server's own reason when it gives one ("You already have several questions being answered; please wait for them."), or the
      // reason a job failed; the general line only when nothing more is known.
      const busy = error instanceof ApiError && error.status === 429;
      const detail = apiErrorDetail(error) ?? (!(error instanceof ApiError) && error instanceof Error && error.message && !/abort/i.test(error.name) ? error.message : null);
      const text = expired
        ? "Your session has ended. Please sign in again."
        : busy
          ? detail ?? "You already have several questions being answered; please wait for them to finish."
          : detail ?? "Could not get an answer just now. Please try again.";
      setMessages((prev) => [...prev, { id: newId(), role: "error", text, retryable: !expired }]);
    },
  });

  // Every change to the conversation is saved, here and on the server, so it can be reopened from History on any device (a chat
  // with no question yet is not saved). A failed server save is retried by the next change or the next visit's import.
  React.useEffect(() => {
    if (messages.length === 0) return;
    if (skipSaveRef.current) {
      skipSaveRef.current = false;
      return;
    }
    const saved = saveConversation(userKey, conversationId, messages);
    setHistory(saved);
    const chat = saved.find((c) => c.id === conversationId);
    if (chat && user) void saveLookupChat(chat).catch(() => undefined);
  }, [messages, conversationId, userKey, user]);

  // On opening HP Genie: this browser's chats (including those kept before chats were saved on the server) go up, and the user's
  // chats from other devices come down. Once per visit.
  React.useEffect(() => {
    if (!user) return;
    let cancelled = false;
    importLookupChats(loadConversations(userKey))
      .then((fromServer) => {
        if (cancelled) return;
        // A delete this browser could not get to the server is tried again now, and the chat is not shown meanwhile.
        const pending = pendingDeletions(userKey);
        const chats = (fromServer as StoredConversation[]).filter((c) => !deletedHere(pending, c));
        if (pending["*"] !== undefined) void clearLookupChats().then(() => forgetDeletion(userKey, "*")).catch(() => undefined);
        for (const c of fromServer as StoredConversation[]) {
          if (pending[c.id] !== undefined && deletedHere(pending, c)) void deleteLookupChat(c.id).then(() => forgetDeletion(userKey, c.id)).catch(() => undefined);
        }
        setHistory(replaceWithServerConversations(userKey, chats));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [userKey, user]);

  React.useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, ask.isPending]);

  // Grow the box with the text, up to a limit.
  React.useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
  }, [draft]);

  function send(text: string) {
    const message = text.trim();
    if (!message || ask.isPending) return;
    const history = messages.filter((m): m is Extract<ChatMessage, { role: "user" }> => m.role === "user").map((m) => m.text);
    setMessages((prev) => [...prev, { id: newId(), role: "user", text: message }]);
    setDraft("");
    ask.mutate({ message, history, turns: chatTurns(messages), chatId: conversationId });
  }

  /** Asks the same question(s) again: drops the answer (or the error) and sends the message before it once more. */
  function retry(id: string) {
    if (ask.isPending) return;
    const at = messages.findIndex((m) => m.id === id);
    const before = at > 0 ? messages[at - 1] : undefined;
    if (!before || before.role !== "user") return;
    const history = messages
      .slice(0, at - 1)
      .filter((m): m is Extract<ChatMessage, { role: "user" }> => m.role === "user")
      .map((m) => m.text);
    setMessages(messages.slice(0, at));
    ask.mutate({ message: before.text, history, turns: chatTurns(messages.slice(0, at - 1)), chatId: conversationId });
  }

  function newChat() {
    if (ask.isPending) return;
    ask.reset();
    setConversationId(newConversationId());
    setMessages([]);
    setDraft("");
    setShowHistory(false);
    inputRef.current?.focus();
  }

  function openConversation(c: StoredConversation) {
    if (ask.isPending) return;
    ask.reset();
    skipSaveRef.current = c.messages.length > 0;
    setConversationId(c.id);
    setMessages(c.messages);
    setDraft("");
    setShowHistory(false);
  }

  function removeConversation(id: string) {
    if (id === conversationId && ask.isPending) return;  // the reply would save the chat again under the same id
    setHistory(deleteConversation(userKey, id));
    rememberDeletion(userKey, id);
    void deleteLookupChat(id).then(() => forgetDeletion(userKey, id)).catch(() => undefined);
    if (id === conversationId) newChat();
  }

  const empty = messages.length === 0;

  return (
    <div className="mx-auto -my-4 flex h-[calc(100dvh-8rem)] max-w-4xl flex-col xl:max-w-5xl sm:-my-6 md:h-[calc(100dvh-3.9rem)] lg:-my-8">
      <div className="flex flex-none items-center justify-between py-2">
        <div className="flex items-center gap-2 text-sm text-zinc-500">
          <Scale className="h-4 w-4" aria-hidden />
          <span>Ask about contracts, orders and tariffs. Every answer shows where it came from.</span>
        </div>
        <div className="relative flex items-center gap-2">
          <button
            type="button"
            onClick={() => {
              setHistory(loadConversations(userKey));
              setShowHistory((v) => !v);
            }}
            aria-expanded={showHistory}
            className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-200 bg-white px-3 py-1.5 text-sm font-medium text-zinc-700 transition-colors hover:bg-zinc-50"
          >
            <History className="h-4 w-4" aria-hidden />
            History{history.length ? ` (${history.length})` : ""}
          </button>
          {!empty ? (
            <button
              type="button"
              onClick={newChat}
              className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-200 bg-white px-3 py-1.5 text-sm font-medium text-zinc-700 transition-colors hover:bg-zinc-50"
            >
              <Plus className="h-4 w-4" aria-hidden />
              New chat
            </button>
          ) : null}
          {showHistory ? (
            <HistoryPanel
              conversations={history}
              activeId={conversationId}
              onOpen={openConversation}
              onDelete={removeConversation}
              onClearAll={() => {
                clearConversations(userKey);
                rememberDeletion(userKey, "*");
                void clearLookupChats().then(() => forgetDeletion(userKey, "*")).catch(() => undefined);
                setHistory([]);
                newChat();
              }}
              onClose={() => setShowHistory(false)}
            />
          ) : null}
        </div>
      </div>

      <div ref={scrollRef} className="min-h-0 flex-1 space-y-6 overflow-y-auto px-1 py-4" aria-live="polite">
        {empty ? (
          <div className="flex h-full flex-col items-center justify-center gap-6 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-sky-100 text-sky-700">
              <Scale className="h-6 w-6" aria-hidden />
            </div>
            <div>
              <h2 className="font-serif text-2xl font-semibold text-zinc-900">HP Genie</h2>
              <p className="mt-1 text-sm text-zinc-500">
                Ask one question. Everything you type is treated as one question, and comes back with a detailed explanation and the sources it was read from.
              </p>
            </div>
            {SHOW_STARTER_PROMPTS ? (
              <div className="grid w-full max-w-2xl gap-2">
                {EXAMPLES.map((example) => (
                  <button
                    key={example}
                    type="button"
                    onClick={() => send(example)}
                    className="rounded-xl border border-zinc-200 bg-white px-4 py-3 text-left text-sm text-zinc-700 transition-colors hover:border-sky-200 hover:bg-sky-50"
                  >
                    {example}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => send(EXAMPLES.map((q, i) => `${i + 1}. ${q}`).join("\n"))}
                  className="rounded-xl border border-dashed border-zinc-300 px-4 py-3 text-left text-sm text-zinc-500 transition-colors hover:border-sky-300 hover:text-sky-800"
                >
                  Ask all three at once
                </button>
              </div>
            ) : null}
          </div>
        ) : (
          messages.map((m) => {
            if (m.role === "user") {
              return (
                <div key={m.id} className="flex justify-end">
                  <div className="max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-br-md bg-sky-600 px-4 py-2.5 text-sm text-white">
                    {m.text}
                  </div>
                </div>
              );
            }
            if (m.role === "error") {
              return (
                <div key={m.id} className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                  <TriangleAlert className="mt-0.5 h-4 w-4 flex-none" aria-hidden />
                  <span className="flex-1">{m.text}</span>
                  {m.retryable ? (
                    <button
                      type="button"
                      onClick={() => retry(m.id)}
                      disabled={ask.isPending}
                      className="inline-flex flex-none items-center gap-1.5 rounded-md border border-red-300 bg-white px-2.5 py-1 text-xs font-medium text-red-800 transition-colors hover:bg-red-100 disabled:opacity-50"
                    >
                      <RotateCw className="h-3.5 w-3.5" aria-hidden />
                      Try again
                    </button>
                  ) : null}
                </div>
              );
            }
            return (
              <AssistantTurn
                key={m.id}
                response={m.response}
                onOpen={(c) => setOpen(toOpenCitation(c))}
                onRetry={() => retry(m.id)}
                busy={ask.isPending}
              />
            );
          })
        )}
        {ask.isPending ? <Thinking /> : null}
      </div>

      <form
        className="flex-none pb-1 pt-1"
        onSubmit={(event) => {
          event.preventDefault();
          send(draft);
        }}
      >
        <div className="flex items-end gap-2 rounded-2xl border border-zinc-300 bg-white p-2 shadow-xs focus-within:border-sky-400 focus-within:ring-2 focus-within:ring-sky-100">
          <textarea
            ref={inputRef}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
                event.preventDefault();
                send(draft);
              }
            }}
            rows={1}
            maxLength={4000}
            placeholder="Ask a question, or several (Shift+Enter for a new line)…"
            aria-label="Your question"
            className="max-h-[200px] min-h-[2.5rem] flex-1 resize-none bg-transparent px-2 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:outline-none"
          />
          <button
            type="submit"
            disabled={!draft.trim() || ask.isPending}
            aria-label="Send"
            className="flex h-9 w-9 flex-none items-center justify-center rounded-xl bg-sky-600 text-white transition-colors hover:bg-sky-700 disabled:cursor-not-allowed disabled:bg-zinc-200 disabled:text-zinc-400"
          >
            <ArrowUp className="h-4 w-4" aria-hidden />
          </button>
        </div>
        <div className="mt-1 flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
          <p className="text-[0.6875rem] leading-tight text-zinc-400">
            Answers come only from the indexed documents. Check the cited page before relying on a figure.
          </p>
        </div>
      </form>

      {open ? (
        <SourceDocumentViewer source={open.source} passage={open.passage} startPage={open.startPage} onClose={() => setOpen(null)} />
      ) : null}
    </div>
  );
}
